import { basicUIPlugin } from '@stackflow/plugin-basic-ui'
import '@stackflow/plugin-basic-ui/index.css'
import { basicRendererPlugin } from '@stackflow/plugin-renderer-basic'
import { ActivityComponentType, stackflow } from '@stackflow/react'
import { GetServerSideProps } from 'next'
import { useEffect, useMemo } from 'react'
import AITutorAILevelActivity from '../../../../components/webview/ai-tutor/AITutorAILevelActivity'
import AITutorActActivity from '../../../../components/webview/ai-tutor/AITutorActActivity'
import AITutorActivity from '../../../../components/webview/ai-tutor/AITutorActivity'
import AITutorCallCompleteActivity from '../../../../components/webview/ai-tutor/AITutorCallCompleteActivity'
import AITutorCreateCustomRolePlay from '../../../../components/webview/ai-tutor/AITutorCreateCustomRoleplay'
import AITutorDiscussionInstructionActivity from '../../../../components/webview/ai-tutor/AITutorDiscussionInstructionActivity'
import AITutorDiscussionMaterialCategoryActivity from '../../../../components/webview/ai-tutor/AITutorDiscussionMaterialCategoryActivity'
import AITutorDiscussionMaterialDetailActivity from '../../../../components/webview/ai-tutor/AITutorDiscussionMaterialDetailActivity'
import AITutorDiscussionMaterialSearchActivity from '../../../../components/webview/ai-tutor/AITutorDiscussionMaterialSearchActivity'
import AITutorDiscussionQuestionSelectionActivity from '../../../../components/webview/ai-tutor/AITutorDiscussionQuestionSelectionActivity'
import AITutorListActivity from '../../../../components/webview/ai-tutor/AITutorListActivity'
import AITutorOnboardingActivity from '../../../../components/webview/ai-tutor/AITutorOnboarding'
import AITutorPurchaseActivity from '../../../../components/webview/ai-tutor/AITutorPurchaseActivity'
import AITutorPurchaseCompleteActivity from '../../../../components/webview/ai-tutor/AITutorPurchaseCompleteActivity'
import AITutorReportActivity from '../../../../components/webview/ai-tutor/AITutorReportActivity'
import AITutorRoleplayActivity from '../../../../components/webview/ai-tutor/AITutorRoleplayActivity'
import AITutorRoleplayCompleteActivity from '../../../../components/webview/ai-tutor/AITutorRoleplayDoneActivity'
import AITutorRoleplayInstructionActivity from '../../../../components/webview/ai-tutor/AITutorRoleplayInstructionActivity'
import AITutorRoleplayLivekit from '../../../../components/webview/ai-tutor/AITutorRoleplayLivekit'
import AITutorStreakActivity from '../../../../components/webview/ai-tutor/AITutorStreakActivity'
import { useAppVersionStore } from '../../../../store/useAppVersionStore'

const activities = {
  AITutorActivity,
  AITutorActActivity,
  AITutorPurchaseActivity,
  AITutorReportActivity,
  AITutorListActivity,
  AITutorPurchaseCompleteActivity,
  AITutorRoleplayActivity,
  AITutorRoleplayCompleteActivity,
  AITutorRoleplayInstructionActivity,
  AITutorDiscussionInstructionActivity,
  AITutorDiscussionMaterialSearchActivity,
  AITutorDiscussionMaterialDetailActivity,
  AITutorDiscussionMaterialCategoryActivity,
  AITutorDiscussionQuestionSelectionActivity,
  AITutorAILevelActivity,
  AITutorOnboardingActivity,
  AITutorCreateCustomRolePlay,
  AITutorRoleplayLivekit,
  AITutorStreakActivity,
  AITutorCallCompleteActivity,
}

type SameKeyValue<T extends keyof typeof activities> = {
  [K in T]: K
}

const createActivitiesMap = <T extends keyof typeof activities>(
  acts: Record<T, ActivityComponentType>
): SameKeyValue<T> =>
  (Object.keys(acts) as T[])
    .map((activity) => ({ [activity]: activity }))
    .reduce((ac, cr) => {
      return {
        ...ac,
        ...cr,
      }
    }, {}) as SameKeyValue<T>

export const AI_TUTOR_ACTIVITIES_MAP = createActivitiesMap(activities)

export type AITutorActivityNamesType = keyof typeof activities

export const useAITutorStack = (initialActivityName?: string | string[], initialParams?) => {
  const {
    Stack: AITutorStack,
    useFlow: useAITutorFlow,
    activities: AITutorActivities,
  } = useMemo(() => {
    return stackflow({
      transitionDuration: 350,
      activities: activities,
      plugins: [
        basicRendererPlugin(),
        basicUIPlugin({
          theme: 'cupertino',
        }),
        () => {
          return {
            key: 'query-param-nested-navigation-plugin',
            overrideInitialEvents: ({ initialEvents }) => {
              const temp = {
                ...initialEvents[0],
                activityName: initialActivityName as AITutorActivityNamesType,
                activityParams: initialParams,
              }
              return [temp]
            },
          }
        },
      ],
      initialActivity: () => initialActivityName as AITutorActivityNamesType,
    })
  }, [initialActivityName])

  return { AITutorStack, AITutorActivities, useAITutorFlow }
}

const Index = ({ activity, courseId, lessonId, roleplayId, tabId, jwtToken }) => {
  const { AITutorStack } = useAITutorStack(activity, { courseId, lessonId, roleplayId, tabId })

  // 앱 버전을 검사 합니다.
  const { setAppVersion } = useAppVersionStore()

  useEffect(() => {
    try {
      window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'getAppVersion' }))
    } catch (e) {
      console.log(e)
    }
  }, [])

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      try {
        const msg = JSON.parse(event.data)
        if (msg.type === 'appVersion') {
          setAppVersion(msg.appVersion) // 스토어 업데이트
        }
      } catch {
        // JSON 파싱 실패 시 무시
      }
    }

    window.addEventListener('message', handleMessage)
    document.addEventListener('message', handleMessage)

    return () => {
      window.removeEventListener('message', handleMessage)
      document.removeEventListener('message', handleMessage)
    }
  }, [setAppVersion])

  return (
    <div className="h-full w-full ">
      <AITutorStack />
    </div>
  )
}

export const getServerSideProps: GetServerSideProps = async (props) => {
  const { query } = props
  const {
    activity = 'AITutorActivity',
    courseId = '',
    lessonId = '',
    roleplayId = '',
    tabId = '',
    threadId = '',
    categoryId = '',
    materialId = '',
    activityId = '',
    jwt_token = '',
  } = query
  return {
    props: {
      activity: activity,
      courseId: courseId,
      lessonId: lessonId,
      roleplayId: roleplayId,
      tabId: tabId,
      threadId: threadId,
      categoryId: categoryId,
      materialId: materialId,
      activityId: activityId,
      jwtToken: jwt_token,
    },
  }
}

export default Index
